/**
 * Shop archive — AJAX filter sidebar + pagination.
 * Requires `dbShopFilters` (ajaxUrl, nonce) localized via ArchiveFilters::localize_filters_data().
 */

import { initProductGrid } from '../elementor/widgets/product-grid.js';
import { initButtonAnimations } from '../animations/buttons.js';

export function initArchiveFilters() {
	const root = document.querySelector( '.shop-archive' );
	const config = window.dbShopFilters;
	if ( ! root || ! config ) return;

	const form        = root.querySelector( '#shop-filters-form' );
	const grid         = root.querySelector( '#shop-product-grid' );
	const pagination    = root.querySelector( '#shop-pagination' );
	const countEl      = root.querySelector( '#shop-result-count' );
	const toggleBtn    = root.querySelector( '#shop-filters-toggle' );
	const panel        = root.querySelector( '#shop-filters-panel' );
	const resetBtn     = root.querySelector( '#shop-filters-reset' );
	if ( ! form || ! grid ) return;

	let debounceTimer;
	let requestToken = 0;

	function fetchResults( { pushHistory = true, scroll = true } = {} ) {
		const params = new URLSearchParams( new FormData( form ) );
		params.set( 'action', 'db_filter_products' );
		params.set( 'nonce', config.nonce );

		const token = ++requestToken;
		grid.setAttribute( 'aria-busy', 'true' );
		root.classList.add( 'is-loading' );

		fetch( `${ config.ajaxUrl }?${ params.toString() }`, { credentials: 'same-origin' } )
			.then( ( res ) => res.json() )
			.then( ( json ) => {
				if ( token !== requestToken || ! json.success ) return;

				grid.innerHTML       = json.data.html;
				pagination.innerHTML = json.data.pagination;
				if ( countEl ) countEl.textContent = json.data.countLabel;

				initProductGrid( grid );
				initButtonAnimations( grid );

				if ( pushHistory ) {
					const url = new URL( window.location.href );
					url.search = serializeForUrl( params );
					window.history.pushState( {}, '', url );
				}

				if ( scroll ) {
					root.scrollIntoView( { behavior: 'smooth', block: 'start' } );
				}
			} )
			.finally( () => {
				if ( token === requestToken ) {
					grid.removeAttribute( 'aria-busy' );
					root.classList.remove( 'is-loading' );
				}
			} );
	}

	// Don't leak internal action/nonce params into the shareable URL.
	function serializeForUrl( params ) {
		const clean = new URLSearchParams( params );
		clean.delete( 'action' );
		clean.delete( 'nonce' );
		return clean.toString();
	}

	function resetPage() {
		const pagedField = form.elements.namedItem( 'paged' );
		if ( pagedField ) pagedField.value = '1';
	}

	form.addEventListener( 'change', ( e ) => {
		resetPage();

		if ( e.target.matches( 'input[name="price_min"], input[name="price_max"]' ) ) {
			clearTimeout( debounceTimer );
			debounceTimer = setTimeout( () => fetchResults(), 400 );
		} else {
			fetchResults();
		}
	} );

	pagination.addEventListener( 'click', ( e ) => {
		const link = e.target.closest( '[data-page]' );
		if ( ! link ) return;
		e.preventDefault();

		const pagedField = form.elements.namedItem( 'paged' );
		if ( pagedField ) pagedField.value = link.dataset.page;
		fetchResults();
	} );

	resetBtn?.addEventListener( 'click', () => {
		form.querySelectorAll( 'input[type="checkbox"]' ).forEach( ( el ) => ( el.checked = false ) );
		form.querySelectorAll( 'input[type="number"]' ).forEach( ( el ) => ( el.value = '' ) );
		const sortField = form.elements.namedItem( 'orderby' );
		if ( sortField ) sortField.value = 'menu_order';
		resetPage();
		fetchResults();
	} );

	toggleBtn?.addEventListener( 'click', () => {
		const isOpen = panel.classList.toggle( 'is-open' );
		toggleBtn.setAttribute( 'aria-expanded', String( isOpen ) );
	} );

	window.addEventListener( 'popstate', () => {
		const params = new URLSearchParams( window.location.search );
		for ( const [ key, value ] of params.entries() ) {
			const fields = form.querySelectorAll( `[name="${ CSS.escape( key ) }"]` );
			fields.forEach( ( field ) => {
				if ( field.type === 'checkbox' ) {
					field.checked = params.getAll( key ).includes( field.value );
				} else {
					field.value = value;
				}
			} );
		}
		fetchResults( { pushHistory: false, scroll: false } );
	} );
}
